// components/ChatList.jsx - Updated for independent scrolling
import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Add as AddIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

const ChatList = ({ chats, selectedChatId, onSelectChat, loading }) => {
  const [search, setSearch] = useState('');
  
  // Filter chats by search term - with null check
  const filteredChats = chats.filter((chat) => {
    if (!chat || !chat.name) return false;
    return chat.name.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <Box sx={{
      height: '100%', // Take full height of parent container
      display: 'flex',
      flexDirection: 'column',
      borderRight: 1,
      borderColor: 'divider',
      overflow: 'hidden', // Prevent box from scrolling
    }}>
      {/* Header - Fixed */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6">Patients</Typography>
        <Box>
          <IconButton size="small">
            <MenuIcon />
          </IconButton>
          <IconButton size="small">
            <AddIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Search - Fixed */}
      <Box sx={{ 
        px: 2, 
        py: 1,
        borderBottom: 1,
        borderColor: 'divider',
      }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search patients"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Chat list - Scrollable */}
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto', // Allow this section to scroll independently
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredChats.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {search ? 'No patients match your search' : 'No patients available'}
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {filteredChats.map((chat) => (
              <ListItem
                button
                key={chat.id}
                selected={chat.chatUuid === selectedChatId}
                onClick={() => onSelectChat(chat.chatUuid)}
                alignItems="flex-start"
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar src={chat.avatar} alt={chat.name} sx={{ bgcolor: 'primary.main' }}>
                    {chat.name ? chat.name.slice(0, 2).toUpperCase() : '??'}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography 
                        noWrap 
                        sx={{ 
                          fontWeight: chat.unreadCount > 0 ? 'bold' : 'normal',
                          color: chat.unreadCount > 0 ? 'text.primary' : 'inherit',
                        }}
                      >
                        {chat.name || 'Unknown'}
                      </Typography>
                      {chat.unreadCount > 0 && (
                        <Box
                          sx={{
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            borderRadius: '50%',
                            minWidth: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 12,
                            fontWeight: 'bold',
                          }}
                        >
                          {chat.unreadCount}
                        </Box>
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography
                        variant="body2"
                        noWrap
                        color={chat.unreadCount > 0 ? 'text.primary' : 'text.secondary'}
                        sx={{ 
                          fontWeight: chat.unreadCount > 0 ? 'medium' : 'normal',
                          display: 'block'
                        }}
                      >
                        {chat.email || chat.phone || 'No contact information'}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {chat.status === 'online' ? 'Online' : 'Offline'}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default ChatList;